import { X } from "lucide-react";
import React, { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useChatButton } from "../context/ChatButtonContext";
import { useTheme } from "../context/ThemeContext";

const ChatButton = ({
  primaryColor,
  disabled = false,
  onOpen,
  onClose,
}) => {
  const { isVisible, hideChatButton, currentSymbol } = useChatButton();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const popupRef = useRef(null);

  const handleCloseChat = useCallback(() => {
    hideChatButton();
    onClose?.();
  }, [hideChatButton, onClose]);

  // Drag functionality - only from header
  const handleMouseDown = useCallback((e) => {
    // Only allow dragging from the header area
    if (!e.target.closest('.chat-popup-header')) return;
    if (e.target.closest('button')) return; // Don't drag if clicking on buttons
    setIsDragging(true);
    const rect = popupRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep popup within viewport bounds
    const maxX = window.innerWidth - 400;
    const maxY = window.innerHeight - (window.innerHeight * 0.8);

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add event listeners for drag functionality
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Listen for chat widget overview click event
  React.useEffect(() => {
    const handleOverviewClick = (event) => {
      console.log('Overview button clicked in chat widget');
      navigate('/discover');
    };

    const handleScannersClick = (event) => {
      console.log('Scanners button clicked in chat widget');
      navigate(`/scanners?scrip=${currentSymbol.scrip}&exchange=${currentSymbol.exchange}&code=${currentSymbol.code}`);
    };
    const handleChartsClick = (event) => {
      console.log('charts button clicked in chat widget');
      navigate(`/charts?code=${currentSymbol.code}`);
    };

    if (popupRef.current) {
      popupRef.current.addEventListener('chat-widget-overview-click', handleOverviewClick);
      popupRef.current.addEventListener('chat-widget-scanners-click', handleScannersClick);
      popupRef.current.addEventListener('chat-widget-charts-click', handleChartsClick);
      return () => {
        if (popupRef.current) {
          popupRef.current.removeEventListener('chat-widget-overview-click', handleOverviewClick);
          popupRef.current.removeEventListener('chat-widget-scanners-click', handleScannersClick);
          popupRef.current.removeEventListener('chat-widget-charts-click', handleChartsClick);
        }
      };
    }
  }, [isVisible]);

  // X icon for close button
  const XIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  return (
    <>
      {/* Chat Widget Popup */}
      {isVisible && (
        <div
          ref={popupRef}
          className="fixed z-[10000] rounded-xl shadow-2xl flex flex-col overflow-hidden"
          onWheel={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'var(--modalBg)',
            border: '1px solid var(--box-border)',
            width: "400px",
            height: "95vh",
            minHeight: "600px",
            maxHeight: "95vh",
            maxWidth: "400px",
            left: `${position.x}px`,
            top: `${position.y}px`,
            animation: "fadeIn 0.3s ease-out",
            userSelect: isDragging ? "none" : "auto",
          }}>
          {/* Header Area - Draggable */}
          <div
            className="chat-popup-header relative flex items-center justify-between p-4 border-b cursor-move"
            style={{
              borderColor: 'var(--box-border)',
              backgroundColor: 'var(--modalBg)'
            }}
            onMouseDown={handleMouseDown}>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--red)' }}></div>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--yellow)' }}></div>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--p150)' }}></div>
              <span className="ml-2 text-sm font-medium" style={{ color: 'var(--text-color)' }}>
                AI Analysis - {currentSymbol?.scrip || 'NIFTY'}
              </span>
            </div>

            {/* Close Button */}
            <button
              onClick={handleCloseChat}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-6 h-6 rounded-full transition-colors flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: 'var(--btn-bg)',
                color: 'var(--text-color)',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--red)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--btn-bg)';
                e.target.style.color = 'var(--text-color)';
              }}>
              <X className="w-3 h-3" />
            </button>
          </div>

          {/* Chat Widget */}
          <div
            className="w-full flex-1 overflow-y-auto"
            style={{ position: "relative", zIndex: 1 }}>
            <chat-widget
              key={`chat-widget-${currentSymbol?.scrip}-${isVisible}-${theme}`}
              theme={theme}
              symbol={currentSymbol?.scrip || 'NIFTY'}
              primary-color={primaryColor}
              show-overview
              show-charts
              {...(currentSymbol?.exchange === 'NSE' && { 'show-scanners': '' })}
            />
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* Ensure chat widget fits container */
        chat-widget {
          height: 100% !important;
          width: 100% !important;
          display: block !important;
        }
      `}</style>
    </>
  );
};

export default ChatButton;
